import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CreditCard, Wallet, Check } from 'lucide-react';
import { toast } from 'sonner';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  currency: 'USDT' | 'wHANDY';
  title: string;
  description: string;
  onSuccess: () => void;
  apiBase?: string;
  eventId?: number;
  userBalance?: number;
}

function computeDefaultApiBase() {
  const envValue = (import.meta as any)?.env?.VITE_API_URL?.trim?.();
  if (envValue) {
    return envValue.endsWith('/') ? envValue.slice(0, -1) : envValue;
  }

  if (typeof window === 'undefined') {
    return 'https://wallet.handy.app';
  }

  try {
    const url = new URL(window.location.origin);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      url.hostname = 'localhost';
      url.port = '3600';
      url.protocol = 'http:';
      return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
    }
    return url.origin.endsWith('/') ? url.origin.slice(0, -1) : url.origin;
  } catch (error) {
    return 'https://wallet.handy.app';
  }
}

export function PaymentModal({ isOpen, onClose, amount, currency, title, description, onSuccess, apiBase, eventId, userBalance }: PaymentModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'confirm' | 'pin' | 'processing' | 'success'>('confirm');
  const [pin, setPin] = useState('');

  const resolvedApiBase = useMemo(() => {
    const baseCandidate = apiBase?.trim() || (import.meta as any)?.env?.VITE_API_URL?.trim?.() || '';
    if (baseCandidate) {
      return baseCandidate.endsWith('/') ? baseCandidate.slice(0, -1) : baseCandidate;
    }
    const fallback = computeDefaultApiBase();
    return fallback.endsWith('/') ? fallback.slice(0, -1) : fallback;
  }, [apiBase]);

  const buildUrl = useCallback(
    (path: string) => (resolvedApiBase ? `${resolvedApiBase}${path}` : path),
    [resolvedApiBase]
  );

  const handleContinue = () => {
    setStep('pin');
  };

  const handlePayment = async () => {
    if (!pin || pin.length < 6) {
      toast.error('Please enter your 6-10 character PIN');
      return;
    }

    setIsProcessing(true);
    setStep('processing');

    try {
      if (title.includes('Node') && currency === 'wHANDY') {
        const token = localStorage.getItem('handy-jwt-token');
        const response = await fetch(buildUrl('/api/nodes/reserve'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            productId: 1,
            nodeType: 'full',
            amount: amount.toString(),
            pin
          })
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Payment failed');
        }

        toast.success(`Payment successful! TX: ${result.txHash?.slice(0, 10)}...`);
      } else if (title.includes('BOOM') && currency === 'wHANDY') {
        const token = localStorage.getItem('handy-jwt-token');
        const response = await fetch(buildUrl('/api/boom/participate'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            eventId: eventId ?? 1,
            pin
          })
        });

        const text = await response.text();
        let result: any = {};
        try {
          result = JSON.parse(text);
        } catch (jsonError) {
          if (!response.ok) {
            throw new Error(`Payment failed (status ${response.status})`);
          }
        }

        if ((result?.data?.alreadyParticipated) || (result?.message?.includes?.('이미 참여'))) {
          toast.success('이미 참여한 이벤트입니다.');
          setStep('success');
          setTimeout(() => {
            onSuccess();
            onClose();
            setStep('confirm');
            setPin('');
            setIsProcessing(false);
          }, 1500);
          return;
        }

        if (response.status === 400 && result?.error?.includes('이미 참여')) {
          toast.success('이미 참여한 이벤트입니다.');
          setStep('success');
          setTimeout(() => {
            onSuccess();
            onClose();
            setStep('confirm');
            setPin('');
            setIsProcessing(false);
          }, 1500);
          return;
        }

        if (!response.ok || (result && result.success === false)) {
          throw new Error(result?.error || `Payment failed (status ${response.status})`);
        }

        toast.success('붐 이벤트 참여가 완료되었습니다.');
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      setStep('success');
      setTimeout(() => {
        onSuccess();
        onClose();
        setStep('confirm');
        setPin('');
        setIsProcessing(false);
      }, 2000);
    } catch (error: any) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
      setStep('pin');
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
      setStep('confirm');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm sm:max-w-md mx-auto bg-background border-border w-[90vw] max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-bold text-foreground">
            {step === 'confirm' && '결제를 확인하세요'}
            {step === 'pin' && 'PIN을 입력하세요'}
            {step === 'processing' && '결제를 처리 중입니다'}
            {step === 'success' && '결제가 완료되었습니다'}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {step === 'confirm' && description}
            {step === 'pin' && '6~10자의 PIN을 입력하여 결제를 완료하세요.'}
            {step === 'processing' && '잠시만 기다려 주세요. 결제를 처리 중입니다.'}
            {step === 'success' && '붐 이벤트 참여가 완료되었습니다.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'confirm' && (
          <div className="space-y-6 mt-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6">
              <div className="text-center">
                <h3 className="font-bold text-lg text-foreground mb-2">{title}</h3>
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currency === 'USDT' ? 'bg-green-500' : 'bg-blue-500'
                  }`}>
                    <span className="text-white font-bold text-sm">
                      {currency === 'USDT' ? '₮' : 'V'}
                    </span>
                  </div>
                  <span className="text-3xl font-bold text-foreground">{amount.toLocaleString()}</span>
                  <span className="text-lg font-medium text-muted-foreground">{currency}</span>
                </div>
                <p className="text-muted-foreground text-sm">≈ ${(amount * (currency === 'USDT' ? 1 : 0.5)).toFixed(2)} USD</p>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-foreground font-medium">결제 수단</Label>
              <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Handy 지갑</p>
                    <p className="text-muted-foreground text-sm">
                      잔액:{' '}
                      {currency === 'wHANDY' && typeof userBalance === 'number'
                        ? `${userBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} wHANDY`
                        : `${currency === 'USDT' ? '2,500.00' : '1,250.75'} ${currency}`}
                    </p>
                  </div>
                  <Check className="h-5 w-5 text-green-500" />
                </div>
              </div>
            </div>

            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mt-0.5">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div>
                  <p className="font-medium text-orange-700 dark:text-orange-300 text-sm">보안 안내</p>
                  <p className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                    이 결제는 블록체인 네트워크를 통해 안전하게 처리됩니다.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleContinue}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white h-12"
              disabled={isProcessing}
            >
              <CreditCard className="w-4 h-4 mr-2" />
              계속 진행
            </Button>

            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full border-border text-muted-foreground hover:bg-accent"
              disabled={isProcessing}
            >
              취소
            </Button>
          </div>
        )}

        {step === 'pin' && (
          <div className="space-y-6 mt-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 text-center">
              <p className="text-muted-foreground text-sm mb-2">결제 금액</p>
              <p className="text-3xl font-bold text-foreground">{amount.toLocaleString()} {currency}</p>
            </div>

            <div>
              <Label htmlFor="pin">PIN 입력</Label>
              <Input
                id="pin"
                type="text"
                inputMode="text"
                value={pin}
                onChange={(e) => setPin(e.target.value.slice(0, 10))}
                placeholder="6~10자 PIN을 입력하세요"
                className="text-center text-2xl tracking-widest"
                maxLength={10}
                autoFocus
              />
            </div>

            <div className="space-y-3">
              <Button
                onClick={handlePayment}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white h-12"
                disabled={!pin || pin.length < 6}
              >
                결제 확인
              </Button>

              <Button
                variant="outline"
                onClick={() => setStep('confirm')}
                className="w-full border-border text-muted-foreground hover:bg-accent"
              >
                돌아가기
              </Button>
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="font-bold text-lg text-foreground mb-2">결제를 처리 중입니다</h3>
            <p className="text-muted-foreground text-sm">잠시만 기다려 주세요.</p>
            <div className="flex items-center justify-center space-x-1 mt-4">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h3 className="font-bold text-lg text-foreground mb-2">결제가 완료되었습니다!</h3>
            <p className="text-muted-foreground text-sm">{title} 참여가 완료되었습니다.</p>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 mt-4">
              <p className="text-green-700 dark:text-green-300 text-sm">
                트랜잭션 ID: #{Math.random().toString(36).substr(2, 9).toUpperCase()}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}